/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice } from '@reduxjs/toolkit';

import { mapisPickupOrIsDelivery } from 'common/components-v2/TruckV2/TruckFulfillment/DistributionCenters/DistributionCentersUtil';
import { generateRandomUUID } from 'common/utils/Utils';

const initialState = {
    initialized: false,
    lineItems: [],
    products: [],
    deliveryOptions: [],
    fulfillmentDetails: null,
    fulfillmentDistributionCenter: null,
    validateFulfillmentMessage: '',
    requireValidateFulfillment: false,
    hasPackagedProducts: false,
    displayLoadingOverlay: false,
};

/**
 * Helper function to zero out price for labor products
 */
const zeroPrice = item => {
    return {
        ...item,
        product: {
            ...item.product,
            adjustPrice: true,
            price: {
                ...item.product.price,
                price: 0,
                priceIncludingTax: 0,
                priceTax: 0,
            },
        },
    };
};

const truckSlice = createSlice({
    name: 'truck',
    initialState,
    reducers: {
        /**
         * Initializes the truck store with line items and fulfillment details
         */
        init: (state, action) => {
            const options = action.payload;
            const fulfillmentDistributionCenterWithConfigs =
                mapisPickupOrIsDelivery([
                    options.fulfillmentDistributionCenter,
                ]);

            return {
                ...state,
                initialized: true,
                lineItems: options.lineItems || [],
                products: [],
                displayLoadingOverlay: options.displayLoadingOverlay,
                fulfillmentDetails: options.fulfillmentDetails,
                deliveryOptions: options.deliveryOptions,
                fulfillmentDistributionCenter:
                    fulfillmentDistributionCenterWithConfigs[0],
                hasPackagedProducts: false,
                validateFulfillmentMessage: '',
                requireValidateFulfillment: true,
            };
        },

        /**
         * Updates line items
         * Payload: array of line items
         */
        updateLineItems: (state, action) => {
            return {
                ...state,
                lineItems: action.payload || [],
                requireValidateFulfillment: true,
            };
        },

        /**
         * Updates line items and products with calculated costs
         * Payload: array of calculated line items
         */
        updateLineItemsCost: (state, action) => {
            const calculatedLineItems = action.payload;

            const lineItems = state.lineItems.map(li => {
                const update = calculatedLineItems.find(
                    cLi => cLi.id === li.id
                );
                if (update) {
                    return {
                        ...li,
                        cost: update.cost,
                        price: update.price,
                        payout: update.payout,
                    };
                }
                return li;
            });

            const products = state.products.map(li => {
                const update = calculatedLineItems.find(
                    cLi => cLi.uuid && cLi.uuid === li.product?.uuid
                );
                if (update) {
                    return {
                        ...li,
                        product: {
                            ...li.product,
                            cost: update.cost,
                            price: update.price,
                            payout: update.payout,
                        },
                    };
                }
                return li;
            });

            return {
                ...state,
                lineItems: lineItems || [],
                products: products || [],
            };
        },

        /**
         * Removes a line item by id
         * Payload: string (lineItemId)
         */
        remove: (state, action) => {
            const removeLineItemId = action.payload;
            const lineItems = [...state.lineItems];
            const _lineItems = lineItems.filter(
                li =>
                    li.id !== removeLineItemId &&
                    li.bundleJobLineItemId !== removeLineItemId
            );

            return {
                ...state,
                lineItems: _lineItems,
                requireValidateFulfillment: true,
            };
        },

        /**
         * Adds a product to the truck (enqueues it)
         * Payload: [product, supplied] where supplied contains quantity info
         */
        enqueue: (state, action) => {
            const args = action.payload;
            const doNotMerge = ['SUPPLIED', 'CUSTOM'];
            let products;

            // if product is already in truck increment quantity
            if (
                state.products.some(p => p.product.id === args[0].id) &&
                !doNotMerge.includes(args[0].productCategoryId)
            ) {
                products = state.products.map(p =>
                    p.product.id === args[0].id
                        ? {
                              ...p,
                              supplied: {
                                  ...p.supplied,
                                  quantity:
                                      p.supplied.quantity + args[1].quantity,
                              },
                          }
                        : p
                );
            } else {
                // else add it to products array
                products = [
                    ...state.products,
                    {
                        product: { ...args[0], uuid: generateRandomUUID() },
                        supplied: args[1],
                    },
                ];
            }

            return {
                ...state,
                products,
                requireValidateFulfillment: true,
            };
        },

        /**
         * Adds multiple products to the truck
         * Payload: array of { product, supplied } objects
         */
        enqueueMany: (state, action) => {
            const args = action.payload;
            let products = [...state.products];

            for (let i = 0; i < args.length; i++) {
                if (products.some(p => p.product.id === args[i].product.id)) {
                    products = products.map(p =>
                        p.product.id === args[i].product.id
                            ? {
                                  ...p,
                                  supplied: {
                                      ...p.supplied,
                                      quantity:
                                          p.supplied.quantity +
                                          args[i].supplied.quantity,
                                  },
                              }
                            : p
                    );
                } else {
                    products.push({
                        product: {
                            ...args[i].product,
                            uuid: generateRandomUUID(),
                        },
                        supplied: args[i].supplied,
                    });
                }
            }

            if (state.hasPackagedProducts) {
                const productsWithZeroLabor = products.map(item => {
                    if (item.product.productCategoryId === 'LABOR')
                        return zeroPrice(item);
                    return item;
                });

                return {
                    ...state,
                    products: productsWithZeroLabor,
                    requireValidateFulfillment: true,
                };
            }

            return {
                ...state,
                products,
                requireValidateFulfillment: true,
            };
        },

        /**
         * Adds a packaged product with its sub-products
         * Payload: { product, supplied, packagedProducts }
         */
        enqueuePackagedProduct: (state, action) => {
            const args = action.payload;

            // zero the price of any existing labor products
            const products = state.products.map(item => {
                if (item.product.productCategoryId === 'LABOR')
                    return zeroPrice(item);
                return item;
            });

            // add package product
            products.push({
                product: { ...args.product, uuid: generateRandomUUID() },
                supplied: args.supplied,
            });

            // add the package sub-products
            args.packagedProducts.forEach(item => {
                products.push({
                    product: {
                        ...zeroPrice(item).product,
                        uuid: generateRandomUUID(),
                    },
                    supplied: {
                        description: null,
                        quantity: 1,
                    },
                });
            });

            return {
                ...state,
                products,
                hasPackagedProducts: true,
                requireValidateFulfillment: true,
            };
        },

        /**
         * Removes a product from the truck by uuid
         * Payload: string (uuid)
         */
        dequeue: (state, action) => {
            const uuid = action.payload;
            const products = [...state.products];
            const productIndex = products.findIndex(
                product => product.product.uuid === uuid
            );

            if (productIndex !== -1) {
                products.splice(productIndex, 1);
            }

            return {
                ...state,
                products,
                hasPackagedProducts: !!products.find(
                    item => item.product.productCategoryId === 'PACKAGES'
                ),
                requireValidateFulfillment: true,
            };
        },

        /**
         * Updates fulfillment details and distribution center
         * Payload: { fulfillmentDetails, fulfillmentDistributionCenter }
         */
        updateFulfillmentDetails: (state, action) => {
            const options = action.payload;
            return {
                ...state,
                fulfillmentDetails: options.fulfillmentDetails,
                fulfillmentDistributionCenter: mapisPickupOrIsDelivery([
                    options.fulfillmentDistributionCenter,
                ])[0],
                validateFulfillmentMessage: '',
                requireValidateFulfillment: false,
            };
        },

        /**
         * Updates residential delivery option
         * Payload: boolean (residentialDelivery)
         */
        residentialDeliveryOnChange: (state, action) => {
            const delivery = action.payload;
            return {
                ...state,
                fulfillmentDetails: {
                    ...state.fulfillmentDetails,
                    residentialDelivery: delivery,
                },
            };
        },

        /**
         * Resets products to empty array
         */
        resetProduct: state => {
            return {
                ...state,
                products: [],
                requireValidateFulfillment: true,
            };
        },

        /**
         * Clears truck state (products, line items, fulfillment details)
         */
        clear: state => {
            return {
                ...state,
                products: [],
                lineItems: [],
                deliveryOptions: [],
                fulfillmentDetails: null,
                fulfillmentDistributionCenter: null,
                validateFulfillmentMessage: '',
                requireValidateFulfillment: false,
                initialized: false,
            };
        },

        /**
         * Resets truck to initial state
         */
        reset: () => {
            return initialState;
        },

        /**
         * Sets fulfillment validation message
         * Payload: string (validateFulfillmentMessage)
         */
        setValidFulfillment: (state, action) => {
            return {
                ...state,
                validateFulfillmentMessage: action.payload,
                requireValidateFulfillment: false,
            };
        },

        /**
         * Sets whether fulfillment validation is required
         * Payload: boolean (requireValidateFulfillment)
         */
        setRequireValidateFulfillment: (state, action) => {
            return {
                ...state,
                requireValidateFulfillment: action.payload,
            };
        },
    },
});

export const {
    init,
    updateLineItems,
    updateLineItemsCost,
    remove,
    enqueue,
    enqueueMany,
    enqueuePackagedProduct,
    dequeue,
    updateFulfillmentDetails,
    residentialDeliveryOnChange,
    resetProduct,
    clear,
    reset,
    setValidFulfillment,
    setRequireValidateFulfillment,
} = truckSlice.actions;

export default truckSlice.reducer;
